import { useEffect } from 'react';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { removeRetailPricesForBusinessUnits } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/utils';
import { buildBUEntityConditions } from 'src/components/EntityPage/Filters/businessUnitFilters';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';

export const useValidateDataset = () => {
  const segments = useAppSelector((state) => {
    const { mySegments, teamSegments, teamSearchTermLists, mySearchTermLists } = state.segments;
    return [...mySegments, ...teamSegments, ...teamSearchTermLists, ...mySearchTermLists];
  });
  const { businessUnits, savedSearchesById } = useAppSelector((state) => state.segments);
  const {
    selections: { watch, setValue, setError }
  } = usePresentationFormContext();
  const { showSnackbar } = useSnackbar();

  const entityType = watch('entityType');
  const entityId = watch('entityId');

  const { isLoading, data } = useAtlasV2TableData<{ metadata: any }>({
    fields: [
      {
        name: 'retailSales',
        displayName: 'RETAIL SALES'
      }
    ],
    groupByFieldName: 'brandId',
    indexName: 'sales',
    requestId: 'dataset-brands',
    itemsPerPage: 10,
    pageNumber: 1,
    sortFieldName: 'retailSales',
    suffixOverride: 'value',
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !!entityId.id
    },
    buildRequest: (builder) => {
      builder.removeConditionTermFilters((termFilter) => {
        return termFilter.fieldName === 'brandId';
      });
      if (entityType.id === 'segment') {
        const segment = segments.find((seg) => seg.id === entityId.id);
        if (segment) {
          if (segment.conditions.rangeFilters) {
            segment.conditions.rangeFilters.forEach((rangeFilter) => {
              builder.replaceConditionRangeFilter(rangeFilter.fieldName, rangeFilter.minValue, rangeFilter.maxValue);
            });
          }
          if (segment.conditions.termFilters) {
            segment.conditions.termFilters.forEach((termFilter) => {
              builder.replaceConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
            });
          }
        }
      } else if (entityType.id === 'businessunit') {
        const selectedBusinessUnit = businessUnits.find((businessUnit) => businessUnit.id === entityId.id);

        if (selectedBusinessUnit) {
          const nestedFilterConditions = buildBUEntityConditions(savedSearchesById, selectedBusinessUnit);

          builder.addPreCalculatedNestedFilterCondition(removeRetailPricesForBusinessUnits(nestedFilterConditions));
        }
      }

      return builder;
    }
  });

  useEffect(() => {
    if (entityId.id && data.length !== 10 && !isLoading) {
      const errorMessage = 'Please select a dataset with more than 10 brands';
      showSnackbar({ message: errorMessage, type: 'error' });

      setError('entityId', { message: errorMessage });
      setValue('entityId', { id: '', name: '' });
    }
  }, [data, entityId.id, isLoading]);

  return {
    isValidating: isLoading,
  };
};
